import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import { PortalLayout, PersonLabel } from "../components/Portal";
import { Card, Toggle, SwitchRow, Badge, Btn, Input, Select, Spinner, formatWhen, G_DARK } from "../components/Shared";
import { ROLE_LABELS, grantSeesWholeGroup, isGlobalAdmin, profileConfirmed } from "../auth/roles";
// The three files, and the sentence saying which of them to archive. It renders
// `FileLink`s, so nothing here navigates to an API path — a navigation carries
// no token and downloads a JSON refusal.
import { ArchivalCopy } from "./ProjectDocumentsCard";
import AccountCard from "./AccountCard";
import { useDisplayTimezone, usePortalConfig } from "../portalConfig";
import { pushSupport, urlBase64ToUint8Array, deviceName } from "../push";
import { documentWord, useDocumentWord } from "../documentWord";

/**
 * Your own account: what is being asked of you, and what you have signed up for.
 *
 * Both halves existed only somewhere else before. Documents lived in Group
 * management, which a researcher cannot open at all — so the first they heard of
 * a mandatory policy was the upload gate refusing them, after they had chosen
 * their files. And there was nowhere at all to say whether you wanted the weekly
 * report.
 */
function OutstandingDocuments() {
  // The card's heading follows the current setting; each entry its own kind.
  const word = useDocumentWord();
  // The banner's and the badge's copy of what is owed (AuthContext). This card
  // keeps its own read because it needs the rows, which the counts do not carry.
  const { refreshDuties } = useAuth();
  // Four eyes on documents (2026-10-14): while it is off, what is listed here is
  // left from before, and the sentence about a second person is not the rule.
  const fourEyes = usePortalConfig().document_four_eyes !== false;
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [approvingKey, setApprovingKey] = useState(null);
  // Its own state, not the one the Confirm buttons share. Both cards render an
  // alert, and one variable would put the refusal in both of them — including
  // the card the person pressing Approve is not looking at.
  const [approveError, setApproveError] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    api.get("/api/me/documents")
      .then(setData)
      .catch(() => setData({ outstanding: [], documents: [] }));
  }, []);
  useEffect(load, [load]);

  // Confirming happens HERE now. Until 2026-09-08 the only Confirm button in
  // the portal was inside a project, so a facility-wide policy was confirmed by
  // opening some arbitrary project — and somebody with no project could not
  // confirm one at all.
  const confirm = async d => {
    setError(null);
    setBusyId(d.document_id);
    try {
      await api.post(`/api/groups/${d.group_id}/documents/${d.document_id}/acknowledge`);
      load();
      refreshDuties?.();
    } catch (e) { setError(e.message); } finally { setBusyId(null); }
  };

  // Approving happens here too, since 2026-09-09. The server refuses an admin
  // approving their own version, so a failure has to be SHOWN — the person
  // pressing the button has to be told which rule stopped them rather than
  // watching a row not disappear. Reloads this card's `/api/me/documents` and
  // the shared counts the banner and badge read (`refreshDuties`) — two reads,
  // because they are two copies: this one used to be refreshed alone, on the
  // claim that the layout read it too, and a card that clears itself while the
  // badge still says 1 is worse than one that does not clear (DUTIES, 2026-10-06).
  const approve = async v => {
    setApproveError(null);
    setApprovingKey(`${v.document_id}-${v.version}`);
    try {
      await api.post(
        `/api/groups/${v.group_id}/documents/${v.document_id}/versions/${v.version}/approve`,
        { note: "" });
      load();
      refreshDuties?.();
    } catch (e) { setApproveError(e.message); } finally { setApprovingKey(null); }
  };

  if (!data) return <Card><Spinner /> Loading…</Card>;

  const docs = data.documents || [];

  return (
    <>
    {/* Quotations waiting on this person's signature. /api/me/documents has
        returned them since the notification banner was added and nothing
        listed them — so somebody told "1 quotation to sign" had nowhere to go
        and find it. */}
    {(data.signatures || []).length > 0 && (
      <Card style={{ marginBottom: 20 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>{word({ plural: true, capital: true })} to sign</h3>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>
          A {word()} is agreed once everybody it names has signed.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {data.signatures.map(sig => (
            <div key={sig.quotation_id}
                 style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px",
                          display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <strong style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>
                {sig.number}
              </strong>
              {sig.project_name && (
                <span data-break style={{ fontSize: 13, color: "#374151" }}>{sig.project_name}</span>
              )}
              {sig.required && <Badge color="amber">Your signature is required</Badge>}
              <span style={{ flex: 1 }} />
              {/* The signing page itself, not the project: a representative who
                  is not a project member cannot open the project page, and this
                  entry is their only way to the quotation without the e-mail. */}
              {sig.project_id && (
                <Link to={`/projects/${sig.project_id}/quotations/${sig.quotation_id}/sign`}
                      style={{ fontSize: 13, color: G_DARK }}>
                  Open the {documentWord(sig.kind)}
                </Link>
              )}
            </div>
          ))}
        </div>
      </Card>
    )}
    {/* Versions waiting on THIS admin. The same shape as "Quotations to sign"
        above, because it is the same question — what is waiting for me — and a
        control nobody staffs becomes a reason to work around the portal. */}
    {(data.pending_approval || []).length > 0 && (
      <Card style={{ marginBottom: 20 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Document versions to approve</h3>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>
          {fourEyes ? (
            <>A version reaches force when a second person approves it. Nobody is asked to confirm one
            of these until you do.</>
          ) : (
            <>Four eyes on documents is switched off, so a new version is in force at once. These
            were waiting from before; any administrator, their author included, can approve them.</>
          )}
        </p>
        {approveError && (
          <div role="alert" style={{ margin: "0 0 12px", padding: "10px 12px", borderRadius: 8,
                                     background: "rgba(220,38,38,0.06)",
                                     border: "1px solid rgba(220,38,38,0.25)",
                                     color: "#b91c1c", fontSize: 13 }}>
            {approveError}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {data.pending_approval.map(v => (
            <div key={`${v.document_id}-${v.version}`}
                 style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px",
                          display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <Badge color="amber">Waiting</Badge>
              <span style={{ fontSize: 14 }}>{v.title}</span>
              <span style={{ color: "#6b7280", fontSize: 12 }}>v{v.version}</span>
              {/* `{name, email}`: two accounts may share a name (PERSON). */}
              {v.author && <span style={{ color: "#6b7280", fontSize: 12 }}>by <PersonLabel {...v.author} /></span>}
              {v.change_note && <span style={{ fontSize: 13, color: "#374151" }}>{v.change_note}</span>}
              <span style={{ flex: 1 }} />
              {/* The reasoning that made this a link rather than a button was
                  right: approving should happen where the version can be READ,
                  and a button on its own asks somebody to approve a text they
                  have not seen. What changed on 2026-09-09 is that the text can
                  now be read from here — `?version=` serves the pending one,
                  which had no PDF at all before. So the PDF comes first and the
                  Approve button after it, in that order.

                  Comparing with the previous version, and rejecting — which
                  needs a note the author has to act on — stay in the History
                  panel, and the link below is how to get there. */}
              {/* Three files rather than one, because approving is deciding
                  what the facility will be handing out. `?version=` names the
                  pending one — it is not the version in force, and without it
                  there is no PDF here at all. No checksum is printed: this
                  payload carries none, and a screen that cannot tell "there is
                  no md5" from "the md5 was not sent" says nothing about it. */}
              <ArchivalCopy
                path={`/api/groups/${v.group_id}/documents/${v.document_id}/pdf?version=${v.version}`}
                align="flex-end" />
              <Btn variant="primary" onClick={() => approve(v)}
                   disabled={approvingKey === `${v.document_id}-${v.version}`}
                   style={{ padding: "4px 12px", fontSize: 13 }}>
                {approvingKey === `${v.document_id}-${v.version}` ? "…" : "Approve"}
              </Btn>
              <Link to="/admin/users" style={{ fontSize: 12, color: G_DARK }}>
                Compare or reject
              </Link>
            </div>
          ))}
        </div>
      </Card>
    )}
    <Card>
      <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Documents</h3>
      {error && (
        <div role="alert" style={{ margin: "8px 0", padding: "10px 12px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}
      {docs.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
          Nothing is waiting for you.
        </p>
      ) : (
        <>
          <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>
            {data.blocking > 0
              ? `${data.blocking} of these must be confirmed before you can send data.`
              : data.total > 0
                ? "None of these stop you working — they are worth reading when you have a moment."
                : "Everything asked of you is confirmed. The rest is here to re-read or download."}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {docs.map(d => {
              // By the READER's rule (`rule`, 2026-09-29): one policy is `once`
              // for an operator and `per_project` for a researcher, so the
              // document's own `scope` no longer says which this row is. An
              // older payload without `rule` falls back to where it was filed.
              const perProject = d.rule ? d.rule === "per_project" : !!d.project_id;
              return (
              <div key={`${d.document_id}-${d.project_id ?? "group"}`}
                   style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px",
                            display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                {d.blocking && d.acknowledged === false && <Badge color="red">Required</Badge>}
                {d.mode === "notice" && <Badge color="gray">Notice</Badge>}
                {d.acknowledged === true && <Badge color="green">Confirmed</Badge>}
                <span style={{ fontSize: 14 }}>{d.title}</span>
                <span style={{ color: "#6b7280", fontSize: 12 }}>v{d.version}</span>
                {/* "You signed v1, v2 is current" — the question this screen
                    exists for, and nothing in the portal could answer it. */}
                {d.acknowledged === false && d.acknowledged_version != null
                  && d.acknowledged_version !== d.version && (
                  <span style={{ color: "#92400e", fontSize: 12 }}>
                    you confirmed v{d.acknowledged_version}; this is a new version
                  </span>
                )}
                {perProject && d.project_id ? (
                  <Link data-break to={`/projects/${d.project_id}`} style={{ fontSize: 13, color: G_DARK }}>
                    {d.project_name}
                  </Link>
                ) : (
                  <span style={{ color: "#6b7280", fontSize: 12 }}>
                    {d.mode === "acknowledge" ? "once, for the whole facility" : "the whole facility"}
                  </span>
                )}
                <span style={{ flex: 1 }} />
                {/* The only screen a plain user gets, and the most likely place
                    somebody holding a signed document comes to check it.
                    `content_hash` is the sha256 the confirmation was recorded
                    against — `subject_hash` is written from it — and no md5 is
                    claimed either way, because `/api/me/documents` does not
                    send one. */}
                <ArchivalCopy path={d.pdf_url} sha256={d.content_hash} align="flex-end" />
                {/* A per-project obligation is confirmed on the project, because
                    that is what it is about — so this links there rather than
                    offering a button that would have to guess which project. */}
                {d.acknowledged === false && !perProject && (
                  <Btn variant="secondary" disabled={busyId === d.document_id}
                       onClick={() => confirm(d)}
                       style={{ padding: "4px 10px", fontSize: 12 }}>
                    {busyId === d.document_id ? "Confirming…" : "Confirm"}
                  </Btn>
                )}
              </div>
              );
            })}
          </div>
        </>
      )}
    </Card>
    </>
  );
}

// ── The schedule's arithmetic, mirrored from the server (2026-09-29) ─────────
//
// A summary's schedule is a start and a period: every send is start + k·period,
// and "next" is the first of those not in the past. `nextAfter` is a mirror of
// `backend/portal/services/newsletter.py::next_after`, so the card can show the
// next three sends from a draft *before* anything is saved. It must agree with
// the server, which is why it is tested against instants written out by hand:
//   - the start's minute is the tick's :55, in the same local hour;
//   - days and weeks step in local calendar days in the facility's zone, so
//     00:55 stays 00:55 across a change of summer time;
//   - hours step in UTC, where "every 6 hours" means six hours.

const TICK_MINUTE = 55;
const HOUR_MS = 3600000;
const DAY_MS = 24 * HOUR_MS;
const HOURS_PER = { hours: 1, days: 24, weeks: 168 };
const DEFAULT_PERIOD_HOURS = 168;

// The unit a person chose for a summary, remembered in this browser so that a
// reload does not turn 24 hours back into "1 days". Per summary, keyed by its
// user_role_id. Storage can throw (a private window, blocked site data), and
// then the choice is simply not remembered — the default unit is still right.
const UNIT_KEY = id => `portal.summaryUnit.${id}`;
const storedUnit = id => {
  try {
    const u = window.localStorage.getItem(UNIT_KEY(id));
    return Object.prototype.hasOwnProperty.call(HOURS_PER, u) ? u : null;
  } catch { return null; }
};
const rememberUnit = (id, unit) => {
  try { window.localStorage.setItem(UNIT_KEY(id), unit); } catch { /* not remembered */ }
};

// The wall clock in `zone` at an instant. `zone` undefined is the browser's.
function wallOf(date, zone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone || undefined, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(date);
  const get = type => Number(parts.find(p => p.type === type).value);
  return { y: get("year"), m: get("month"), d: get("day"),
           h: get("hour") % 24, mi: get("minute"), s: get("second") };
}

const offsetAt = (ms, zone) => {
  const w = wallOf(new Date(ms), zone);
  return Date.UTC(w.y, w.m - 1, w.d, w.h, w.mi, w.s) - (ms - (((ms % 1000) + 1000) % 1000));
};

// The instant a local wall-clock time names in `zone`. An hour that happens
// twice is its first occurrence and an hour that never happens takes the offset
// from before the change — what Python's `zoneinfo` does with `fold=0`.
function fromWall({ y, m, d, h, mi }, zone) {
  const guess = Date.UTC(y, m - 1, d, h, mi);
  const offsets = [...new Set([-DAY_MS, 0, DAY_MS].map(dt => offsetAt(guess + dt, zone)))];
  const fits = offsets.map(o => guess - o).filter(t => {
    const w = wallOf(new Date(t), zone);
    return w.y === y && w.m === m && w.d === d && w.h === h && w.mi === mi;
  });
  if (fits.length) return new Date(Math.min(...fits));
  return new Date(guess - offsetAt(guess - DAY_MS, zone));
}

const dayNumber = ({ y, m, d }) => Date.UTC(y, m - 1, d) / DAY_MS;
const fromDayNumber = n => {
  const t = new Date(n * DAY_MS);
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
};

const snapStart = (anchor, zone) => fromWall({ ...wallOf(anchor, zone), mi: TICK_MINUTE }, zone);

const periodHours = hours => {
  const n = Number(hours);
  return Number.isInteger(n) && n >= 1 ? n : DEFAULT_PERIOD_HOURS;
};

/** The first `anchor + k·period`, k ≥ 0, at or after `now` — `next_after`. */
export function nextAfter(anchor, hours, now, zone) {
  hours = periodHours(hours);
  const start = snapStart(new Date(anchor), zone);
  if (start >= now) return start;

  let occurrence;
  let k;
  if (hours % 24 === 0) {
    const days = hours / 24;
    const w = wallOf(start, zone);
    const first = dayNumber(w);
    occurrence = i => fromWall({ ...fromDayNumber(first + days * i), h: w.h, mi: w.mi }, zone);
    k = Math.max(0, Math.floor((dayNumber(wallOf(now, zone)) - first) / days));
  } else {
    occurrence = i => new Date(start.getTime() + hours * HOUR_MS * i);
    k = Math.max(0, Math.floor((now - start) / (hours * HOUR_MS)));
  }
  while (k > 0 && occurrence(k - 1) >= now) k -= 1;
  while (occurrence(k) < now) k += 1;
  return occurrence(k);
}

/** The next `count` sends of a schedule, the first not before `now`. */
export function nextSends(anchor, hours, now, zone, count = 3) {
  const out = [];
  let after = now;
  for (let i = 0; i < count; i += 1) {
    const next = nextAfter(anchor, hours, after, zone);
    out.push(next);
    after = new Date(next.getTime() + 1);
  }
  return out;
}

// The first local :55 strictly after `now` — the start the server gives a
// summary switched on without one (`newsletter.next_tick`).
function nextTick(now, zone) {
  let t = fromWall({ ...wallOf(now, zone), mi: TICK_MINUTE }, zone);
  while (t <= now) t = new Date(t.getTime() + HOUR_MS);
  return t;
}

const pad2 = n => String(n).padStart(2, "0");

function NewsletterSubscriptions() {
  const { user } = useAuth();
  const zone = useDisplayTimezone();
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);
  // **Three kinds of in-flight, kept apart** (2026-09-29). One `busy` used to
  // cover them all, so every settled step of the number disabled the switch
  // for a PATCH and a GET — and a disabled switch is drawn at half opacity, so
  // the green switch flickered while the maintainer raised the days. The
  // switch is disabled only while its OWN change is in flight; a schedule save
  // says so in words on its row instead.
  const [toggling, setToggling] = useState(null);
  const [testing, setTesting]   = useState(null);
  const [rowState, setRowState] = useState({});
  const markRow = (id, state) => setRowState(r => ({ ...r, [id]: state }));

  // Returns the promise so a save can wait for the re-read before it lets go of
  // the person's draft; the effect wraps it because an effect's return value
  // is its cleanup.
  const load = useCallback(
    () => api.get("/api/me/newsletter").then(setData).catch(e => setError(e.message)),
    [],
  );
  useEffect(() => { load(); }, [load]);

  // The switch. The period goes along because the PATCH has always carried it;
  // the start does not, because the server keeps the one it has.
  const toggle = async (sub, enabled) => {
    setToggling(sub.user_role_id);
    setError(null);
    try {
      await api.patch("/api/me/newsletter", {
        user_role_id: sub.user_role_id, enabled, interval_hours: sub.interval_hours,
      });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setToggling(null);
    }
  };

  // The start and the period. Only what changed is sent besides the switch and
  // the period, so a period edit never re-sends a start read back off the
  // screen.
  const saveSchedule = async (sub, changes) => {
    markRow(sub.user_role_id, "saving");
    setError(null);
    try {
      await api.patch("/api/me/newsletter", {
        user_role_id: sub.user_role_id,
        enabled: sub.enabled,
        interval_hours: sub.interval_hours,
        ...changes,
      });
      await load();
      markRow(sub.user_role_id, "saved");
    } catch (e) {
      markRow(sub.user_role_id, null);
      setError(e.message);
    }
  };

  // Admin only, and it goes to the admin's own address whatever grant is named —
  // the route takes no recipient and must never learn to. Offered on the
  // admin's own subscriptions because that is where a grant id is already on
  // screen; the route itself accepts any, for whenever there is a screen that
  // lists somebody else's.
  const [tested, setTested] = useState(null);
  const sendTest = async sub => {
    setTesting(sub.user_role_id);
    setError(null);
    setTested(null);
    try {
      const r = await api.post("/api/admin/newsletter/test",
                               { user_role_id: sub.user_role_id });
      setTested(r.recipient || "your address");
    } catch (e) {
      setError(e.message);
    } finally {
      setTesting(null);
    }
  };

  // Hours under the surface; hours, days or weeks on the screen. A whole number
  // of weeks or days is shown so, because that is how somebody says it, and
  // anything else stays in hours rather than being rounded into a lie. **Once
  // the person has chosen a unit it is kept** — 24 hours saved as hours came
  // back as "1 days" (suggestions/2026-09-23-the-interval-unit-flips-…) — as
  // long as the saved number is still a whole count of it. The choice outlives
  // a reload too: it is remembered per summary in localStorage.
  const [units, setUnits] = useState({});
  const unitOf = s => {
    const chosen = units[s.user_role_id] || storedUnit(s.user_role_id);
    if (chosen && s.interval_hours % HOURS_PER[chosen] === 0) return chosen;
    if (s.interval_hours % 168 === 0) return "weeks";
    if (s.interval_hours % 24 === 0) return "days";
    return "hours";
  };

  // The start as the card shows it: a local date and an hour in the facility's
  // zone. A row with no start yet counts from its cursor, as the job does, and
  // one with neither from the next tick, as the server would.
  const startOf = s => {
    const anchor = s.anchor_at || s.next_due_at || nextTick(new Date(Date.now()), zone);
    const w = wallOf(new Date(anchor), zone);
    return { date: `${w.y}-${pad2(w.m)}-${pad2(w.d)}`, hour: w.h };
  };

  // The schedule is edited locally and saved ONCE — on blur, on Enter, when a
  // select changes, or when the person has stopped for DRAFT_SETTLE_MS. Saving
  // on every change sent 79 PATCHes in 32 seconds from one Firefox tab on
  // 2026-09-23: each spinner step saved, the input was disabled while it did, a
  // disabled input never sees the mouseup, and the browser's spin auto-repeat
  // kept stepping. So no input is disabled, and the draft carries the whole
  // schedule — the unit as well as the number, the date as well as the hour.
  // `drafts` renders; `draftsRef` is what a timer or a blur reads, because
  // both can run before React has re-rendered with the latest keystroke.
  const DRAFT_SETTLE_MS = 600;
  const [drafts, setDrafts] = useState({});
  const draftsRef = useRef({});
  const timers = useRef({});
  const dataRef = useRef(null);
  dataRef.current = data;
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  const putDraft = (id, draft) => {
    draftsRef.current = { ...draftsRef.current, [id]: draft };
    setDrafts(draftsRef.current);
  };
  const dropDraft = (id, draft) => {
    // Only the draft that was saved: one typed while the save was in flight
    // is newer than the server's answer and stays on screen.
    if (draftsRef.current[id] !== draft) return;
    const { [id]: _gone, ...rest } = draftsRef.current;
    draftsRef.current = rest;
    setDrafts(rest);
  };

  // What the row shows: the draft where there is one, the saved schedule
  // otherwise.
  const shown = s => {
    const draft = draftsRef.current[s.user_role_id] || drafts[s.user_role_id];
    if (draft) return draft;
    const unit = unitOf(s);
    return { amount: String(s.interval_hours / HOURS_PER[unit]), unit, ...startOf(s) };
  };

  const edit = (s, change, { settle = true } = {}) => {
    const id = s.user_role_id;
    const { committed: _c, ...current } = shown(s);
    putDraft(id, { ...current, ...change });
    clearTimeout(timers.current[id]);
    if (settle) timers.current[id] = setTimeout(() => commit(id), DRAFT_SETTLE_MS);
    else commit(id);
  };

  const validDate = v => /^\d{4}-\d{2}-\d{2}$/.test(v || "");
  const anchorText = draft => `${draft.date}T${pad2(draft.hour)}:${TICK_MINUTE}:00`;

  const commit = async id => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    const draft = draftsRef.current[id];
    if (!draft || draft.committed) return;
    const sub = dataRef.current?.subscriptions.find(x => x.user_role_id === id);
    const n = Number(draft.amount);
    if (!sub || !Number.isInteger(n) || n < 1 || !validDate(draft.date)) {
      dropDraft(id, draft);
      return;
    }
    setUnits(u => ({ ...u, [id]: draft.unit }));
    rememberUnit(id, draft.unit);
    const changes = {};
    const hours = n * HOURS_PER[draft.unit];
    if (hours !== sub.interval_hours) changes.interval_hours = hours;
    const saved = startOf(sub);
    if (draft.date !== saved.date || Number(draft.hour) !== saved.hour) {
      changes.anchor_at = anchorText(draft);
    }
    if (Object.keys(changes).length === 0) { dropDraft(id, draft); return; }
    const sent = { ...draft, committed: true };
    putDraft(id, sent);
    await saveSchedule(sub, changes);
    dropDraft(id, sent);
  };

  // A <select> has no auto-repeat, so a unit change is committed at once —
  // through the draft, so it carries whatever number is on screen.
  const editUnit = (s, unit) => {
    const n = Number(shown(s).amount);
    edit(s, { amount: String(Math.max(1, Number.isInteger(n) ? n : 1)), unit },
         { settle: false });
  };

  // The schedule a row would run, draft included, as the preview reads it; null
  // while the draft is not a schedule yet.
  const draftSchedule = s => {
    const d = shown(s);
    const n = Number(d.amount);
    if (!Number.isInteger(n) || n < 1 || !validDate(d.date)) return null;
    const [y, m, day] = d.date.split("-").map(Number);
    return { hours: n * HOURS_PER[d.unit],
             anchor: fromWall({ y, m, d: day, h: Number(d.hour), mi: TICK_MINUTE }, zone),
             anchorText: anchorText(d), unit: d.unit };
  };

  // **"Apply to all my summaries"**: this row's start and period, onto every
  // summary the person has, so they arrive together. The switches stay where
  // they are — an apply copies a schedule, it subscribes nobody.
  const applyToAll = async s => {
    const id = s.user_role_id;
    const schedule = draftSchedule(s);
    if (!schedule) { setError("Choose a start date and a period of at least 1 first."); return; }
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    const draft = draftsRef.current[id];
    markRow(id, "saving");
    setError(null);
    try {
      await api.post("/api/me/newsletter/apply-to-all",
                     { interval_hours: schedule.hours, anchor_at: schedule.anchorText });
      const ids = (dataRef.current?.subscriptions || []).map(x => x.user_role_id);
      setUnits(Object.fromEntries(ids.map(x => [x, schedule.unit])));
      ids.forEach(x => rememberUnit(x, schedule.unit));
      await load();
      markRow(id, "saved");
    } catch (e) {
      markRow(id, null);
      setError(e.message);
    }
    if (draft) dropDraft(id, draft);
  };

  if (!data) return <Card><Spinner /> Loading…</Card>;

  const now = new Date(Date.now());
  const fieldStyle = { padding: "4px 8px", fontSize: 13 };
  const muted = { fontSize: 12, color: "#6b7280" };

  return (
    <Card>
      <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Project summaries</h3>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
        What is running, soonest deadline first, laid out so it pastes straight into a spreadsheet.
        Each one starts when you choose and repeats every so many hours, days or weeks — at least
        every hour, which is as often as the portal looks. Changing the schedule never sends one
        by itself.
      </p>

      {error && <div role="alert" style={{ marginBottom: 12, color: "#dc2626", fontSize: 14 }}>{error}</div>}
      {tested && (
        <div role="status" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8,
                                    fontSize: 13, background: "#ecfdf5", color: "#065f46" }}>
          A test summary is queued for {tested}. It does not count as anybody's scheduled one.
        </div>
      )}

      {!data.has_email && (
        <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, fontSize: 13,
                      background: "#fef3c7", color: "#92400e" }}>
          Your account has no e-mail address, so nothing can be sent to you. Add one in the
          Account card above.
        </div>
      )}
      {!data.sending_enabled && data.subscriptions.length > 0 && (
        <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, fontSize: 13,
                      background: "#f3f4f6", color: "#374151" }}>
          Summaries are switched off for the whole portal at the moment. Your choice here is
          remembered and takes effect when an admin turns them on.
        </div>
      )}

      {data.subscriptions.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
          The summary is about a portfolio of projects, which your account does not have.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* One switch per role, because the report means something different
              for each: your own projects, or the whole group by operator. */}
          {data.subscriptions.map(s => {
            const id = s.user_role_id;
            const name = `${ROLE_LABELS[s.role] || s.role} in ${s.group_name || "no group"}`;
            const d = shown(s);
            const schedule = s.enabled ? draftSchedule(s) : null;
            const sends = schedule ? nextSends(schedule.anchor, schedule.hours, now, zone, 3) : [];
            const fmt = when => formatWhen(when, { zone });
            return (
              <div key={id} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <Toggle
                    checked={s.enabled}
                    disabled={toggling === id}
                    onChange={v => toggle(s, v)}
                    label={name}
                  />
                  <span style={muted}>
                    {grantSeesWholeGroup(s.role)
                      ? "the whole group, grouped by operator"
                      : "the projects you run"}
                  </span>
                  {/* In words, not by dimming the switch — see `toggling`. */}
                  <span aria-live="polite" style={muted}>
                    {rowState[id] === "saving" ? "saving…" : rowState[id] === "saved" ? "saved" : ""}
                  </span>
                  {isGlobalAdmin(user) && (
                    <Btn variant="secondary" disabled={testing === id}
                         onClick={() => sendTest(s)}
                         style={{ padding: "4px 10px", fontSize: 12 }}>
                      Send test
                    </Btn>
                  )}
                </div>
                {s.enabled && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                                paddingLeft: 56 }}>
                    <span style={muted}>from</span>
                    {/* A date and an hour in the facility's zone; the minute is
                        the hourly tick's :55, the only minute the job is awake. */}
                    <Input type="date" value={d.date}
                           aria-label={`Start date, ${name}`}
                           onChange={e => edit(s, { date: e.target.value })}
                           onBlur={() => commit(id)}
                           onKeyDown={e => { if (e.key === "Enter") commit(id); }}
                           style={{ ...fieldStyle, width: 150 }} />
                    <Select
                      options={Array.from({ length: 24 }, (_, h) => ({
                        value: String(h), label: `${pad2(h)}:${TICK_MINUTE}`,
                      }))}
                      value={String(d.hour)}
                      aria-label={`Start hour, ${name}`}
                      onChange={e => edit(s, { hour: Number(e.target.value) }, { settle: false })}
                      style={{ ...fieldStyle, width: 90 }} />
                    <span style={muted}>every</span>
                    {/* Never disabled — see DRAFT_SETTLE_MS above for why. */}
                    <Input type="number" min={1} value={d.amount}
                           aria-label={`How often, ${name}`}
                           onChange={e => edit(s, { amount: e.target.value })}
                           onBlur={() => commit(id)}
                           onKeyDown={e => { if (e.key === "Enter") commit(id); }}
                           style={{ ...fieldStyle, width: 72 }} />
                    <Select
                      options={[{ value: "hours", label: "hours" }, { value: "days", label: "days" },
                                { value: "weeks", label: "weeks" }]}
                      value={d.unit}
                      aria-label={`Unit, ${name}`}
                      onChange={e => editUnit(s, e.target.value)}
                      style={{ ...fieldStyle, width: 90 }} />
                    {data.subscriptions.length > 1 && (
                      <Btn variant="secondary" onClick={() => applyToAll(s)}
                           style={{ padding: "4px 10px", fontSize: 12 }}>
                        Apply to all my summaries
                      </Btn>
                    )}
                  </div>
                )}
                {/* What makes the setting verifiable to the person who set it,
                    and before they save it: the next three sends of the draft,
                    by the server's own arithmetic. */}
                {sends.length > 0 && (
                  <span style={{ ...muted, paddingLeft: 56 }}>
                    {`next ${fmt(sends[0])}, then ${sends.slice(1).map(fmt).join(", ")}`}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

/**
 * Archived projects an admin is being asked to reopen.
 *
 * Beside the document versions rather than in its own corner of the portal:
 * they are the same kind of obligation — somebody proposed, and it waits until
 * a second person agrees — and an admin who holds both should find them in one
 * place. A control nobody staffs becomes a reason to work around the portal.
 *
 * Admin-only, so the fetch is not even attempted otherwise: the route answers
 * 403 and an empty card is better read as "nothing waiting" than as an error
 * somebody has to interpret.
 */
function ReopenRequests() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const zone = useDisplayTimezone();
  const isAdmin = isGlobalAdmin(user);

  const load = useCallback(() => {
    if (!isAdmin) return;
    // Array.isArray rather than trusting the body: this repo already has a
    // ratchet test (renderSafety.test.jsx) because an `undefined.map()`
    // unmounted the whole application once, and a card that empties itself is a
    // far better failure than a blank page.
    api.get("/api/me/reopen-requests")
      .then(r => setRows(Array.isArray(r) ? r : []))
      .catch(() => setRows([]));
  }, [isAdmin]);
  useEffect(load, [load]);

  // The server refuses an admin deciding their own request, and refuses one for
  // a project somebody has already reopened by hand. Both have to be SHOWN —
  // the person pressing the button needs to be told which rule stopped them
  // rather than watching a row not disappear.
  const decide = async (r, verb) => {
    setError(null);
    setBusyId(r.id);
    try {
      await api.post(`/api/admin/reopen-requests/${r.id}/${verb}`, { note: "" });
      load();
    } catch (e) { setError(e.message); } finally { setBusyId(null); }
  };

  if (!isAdmin || rows.length === 0) return null;

  return (
    <Card style={{ marginBottom: 20 }}>
      <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Projects to reopen</h3>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>
        A group admin has asked for an archived project to be opened again. Only you can do it,
        and the reason below is kept with the project either way.
      </p>
      {error && (
        <div role="alert" style={{ margin: "0 0 12px", padding: "10px 12px", borderRadius: 8,
                                   background: "rgba(220,38,38,0.06)",
                                   border: "1px solid rgba(220,38,38,0.25)",
                                   color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map(r => (
          <div key={r.id}
               style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px",
                        display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Badge color="amber">Waiting</Badge>
            <Link data-break to={`/projects/${r.project_id}`} style={{ fontSize: 14, color: G_DARK }}>
              {r.project_name}
            </Link>
            <span style={{ color: "#6b7280", fontSize: 12 }}>
              asked by {r.requested_by ? <PersonLabel {...r.requested_by} /> : "an account since deleted"}
            </span>
            <span style={{ color: "#6b7280", fontSize: 12 }}>{formatWhen(r.created_at, zone)}</span>
            {/* The reason IN FULL, not truncated. It is the whole of what is
                being decided, and the approval card next door already argues
                that a decision should happen where the thing can be read. */}
            <div style={{ flexBasis: "100%", fontSize: 13, color: "#374151" }}>{r.reason}</div>
            {!r.still_archived && (
              <span style={{ fontSize: 12, color: "#b45309" }}>
                Already reopened by somebody else — refuse this one.
              </span>
            )}
            <span style={{ flex: 1 }} />
            <Btn variant="primary" onClick={() => decide(r, "approve")}
                 disabled={busyId === r.id}
                 style={{ padding: "4px 12px", fontSize: 13 }}>
              {busyId === r.id ? "…" : "Reopen"}
            </Btn>
            <Btn onClick={() => decide(r, "reject")} disabled={busyId === r.id}
                 style={{ padding: "4px 12px", fontSize: 13 }}>
              Refuse
            </Btn>
          </div>
        ))}
      </div>
    </Card>
  );
}


/**
 * Proposed changes to a policy, waiting for this admin.
 *
 * Beside the versions and the reopen requests, because all three are the same
 * obligation: somebody proposed, and it waits until a second person agrees. A
 * control nobody staffs becomes a reason to work around the portal.
 */
function DocumentChanges() {
  const { user } = useAuth();
  const fourEyes = usePortalConfig().document_four_eyes !== false;
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const zone = useDisplayTimezone();
  const isAdmin = isGlobalAdmin(user);

  const load = useCallback(() => {
    if (!isAdmin) return;
    api.get("/api/me/document-changes")
      .then(r => setRows(Array.isArray(r) ? r : []))
      .catch(() => setRows([]));
  }, [isAdmin]);
  useEffect(load, [load]);

  const decide = async (r, verb) => {
    setError(null);
    setBusyId(r.id);
    try {
      await api.post(`/api/admin/document-changes/${r.id}/${verb}`, { reason: "" });
      load();
    } catch (e) { setError(e.message); } finally { setBusyId(null); }
  };

  // The asker's own requests are not theirs to approve while four eyes is on.
  // The server leaves them out (2026-10-30); mirrored by id so an Approve that
  // always answers 403 never comes back. They take it back on the group's
  // Policy documents screen.
  const shown = fourEyes && user?.id != null
    ? rows.filter(r => r.requested_by_id !== user.id) : rows;

  if (!isAdmin || shown.length === 0) return null;

  const WHAT = { delete: "Delete", withdraw: "Withdraw", update: "Change who it binds" };

  return (
    <Card style={{ marginBottom: 20 }}>
      <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Policy changes to approve</h3>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>
        {fourEyes ? (
          <>A group admin has asked to change a policy. Publishing has needed a second person since
          2026-09-08; deleting, withdrawing and retargeting do now too.</>
        ) : (
          <>Four eyes on documents is switched off, so new changes apply at once. These were asked
          for before; any administrator, the one who asked included, can decide them.</>
        )}
      </p>
      {error && (
        <div role="alert" style={{ margin: "0 0 12px", padding: "10px 12px", borderRadius: 8,
                                   background: "rgba(220,38,38,0.06)",
                                   border: "1px solid rgba(220,38,38,0.25)",
                                   color: "#b91c1c", fontSize: 13 }}>{error}</div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {shown.map(r => (
          <div key={r.id}
               style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px",
                        display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Badge color={r.kind === "delete" ? "red" : "amber"}>{WHAT[r.kind] || r.kind}</Badge>
            <span style={{ fontSize: 14 }}>{r.document_title}</span>
            <span style={{ color: "#6b7280", fontSize: 12 }}>
              asked by {r.requested_by ? <PersonLabel {...r.requested_by} /> : "an account since deleted"}
            </span>
            <span style={{ color: "#6b7280", fontSize: 12 }}>{formatWhen(r.requested_at, zone)}</span>
            {/* **The number this approver is actually deciding about.** Deleting
                destroys every signature with the document, and that is the one
                fact a reviewer cannot get anywhere else on this screen. */}
            {r.kind === "delete" && r.destroys_signatures > 0 && (
              <span style={{ flexBasis: "100%", fontSize: 13, color: "#b91c1c" }}>
                This destroys {r.destroys_signatures} signature
                {r.destroys_signatures === 1 ? "" : "s"}, permanently. The audit entry will be
                the only remaining record of them.
              </span>
            )}
            {r.reason && (
              <div style={{ flexBasis: "100%", fontSize: 13, color: "#374151" }}>{r.reason}</div>
            )}
            <span style={{ flex: 1 }} />
            <Btn variant="primary" onClick={() => decide(r, "approve")} disabled={busyId === r.id}
                 style={{ padding: "4px 12px", fontSize: 13 }}>
              {busyId === r.id ? "…" : "Approve"}
            </Btn>
            <Btn onClick={() => decide(r, "reject")} disabled={busyId === r.id}
                 style={{ padding: "4px 12px", fontSize: 13 }}>
              Refuse
            </Btn>
          </div>
        ))}
      </div>
    </Card>
  );
}


/**
 * Profile → *Notifications on this device* (wave D, 2026-09-30).
 *
 * One switch per device, because a push subscription belongs to one browser on
 * one device: switching it on here says nothing about the laptop. The list
 * below is every device of this person, so a lost phone can be removed from any
 * of them.
 *
 * **The permission prompt is asked from the tap on the switch, never on load.**
 * iOS refuses a prompt that is not a user gesture, and a prompt nobody asked
 * for is the one people answer "Block" — which only the browser's settings can
 * undo. Reading `Notification.permission` asks nothing, so the state is shown
 * from the first render.
 */
// True when the key a subscription was made with (an ArrayBuffer, as
// `PushSubscription.options.applicationServerKey` holds it, or null when the
// browser does not say) is `key`. Unknown is treated as different: replacing a
// good subscription costs a new endpoint, keeping a dead one costs every push.
function sameKey(held, key) {
  if (!held) return false;
  const a = new Uint8Array(held);
  return a.length === key.length && a.every((b, i) => b === key[i]);
}

function PushOnThisDevice() {
  const word = useDocumentWord();
  const zone = useDisplayTimezone();
  const [rows, setRows] = useState([]);
  const [publicKey, setPublicKey] = useState(null);
  const [current, setCurrent] = useState(null);
  const [support, setSupport] = useState(() => pushSupport());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  // **Not tappable before both answers are in.** A tap before `/api/me/push`
  // answered threw "no push key yet" at the person, and a tap before
  // `getSubscription` settled could subscribe afresh beside the subscription
  // the browser already held. Found 2026-10-06 as a test that failed 2 of 5
  // full runs: under load the click landed in exactly that window.
  const [keyRead, setKeyRead] = useState(false);
  const [subRead, setSubRead] = useState(false);

  const load = useCallback(() => {
    api.get("/api/me/push")
      .then(r => {
        setPublicKey(r && typeof r.public_key === "string" ? r.public_key : null);
        // Rows are checked for the one field this card keys on, so an answer of
        // another shape empties the list rather than drawing nonsense.
        const list = r && Array.isArray(r.subscriptions) ? r.subscriptions : [];
        setRows(list.filter(d => d && typeof d.endpoint === "string"));
      })
      .catch(() => setRows([]))
      .finally(() => setKeyRead(true));
  }, []);
  useEffect(load, [load]);

  // What this browser already holds. `getSubscription` asks nothing.
  useEffect(() => {
    if (support === "unsupported") return undefined;
    let live = true;
    navigator.serviceWorker.ready
      .then(reg => reg.pushManager.getSubscription())
      .then(sub => { if (live) setCurrent(sub || null); })
      .catch(() => {})
      .finally(() => { if (live) setSubRead(true); });
    return () => { live = false; };
  }, [support]);

  const mine = current ? rows.find(r => r.endpoint === current.endpoint) : null;
  const on = !!mine;

  const switchOn = async () => {
    // The prompt, from the tap and from nowhere else.
    const answer = await window.Notification.requestPermission();
    if (answer !== "granted") {
      if (answer === "denied") setSupport("blocked");
      else setMessage("Notifications were not allowed, so nothing was switched on.");
      return;
    }
    if (!publicKey) throw new Error("The portal has no push key yet. Try again in a moment.");
    const reg = await navigator.serviceWorker.ready;
    const key = urlBase64ToUint8Array(publicKey);
    let sub = await reg.pushManager.getSubscription();
    // A subscription this browser made under another key — before a restore,
    // which regenerates the portal's pair — is one the push service refuses
    // every push to. Reusing it would store a device that is never reached.
    if (sub && !sameKey(sub.options && sub.options.applicationServerKey, key)) {
      await sub.unsubscribe().catch(() => false);
      sub = null;
    }
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
    const json = sub.toJSON();
    // The server's column is 256 characters and refuses more; the name shown
    // for a device is read from the start of the string.
    await api.post("/api/me/push/subscriptions",
                   { endpoint: json.endpoint, keys: json.keys,
                     user_agent: (navigator.userAgent || "").slice(0, 256) });
    setCurrent(sub);
  };

  const switchOff = async () => {
    if (mine) await api.delete(`/api/me/push/subscriptions/${mine.id}`);
    if (current) await current.unsubscribe().catch(() => false);
    setCurrent(null);
  };

  const toggle = async next => {
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      if (next) await switchOn(); else await switchOff();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      load();
    }
  };

  const remove = async row => {
    setError(null);
    setMessage(null);
    try {
      await api.delete(`/api/me/push/subscriptions/${row.id}`);
      // Removing this device from the list is switching it off here too, or
      // the browser would keep a subscription the server no longer knows.
      if (current && row.endpoint === current.endpoint) {
        await current.unsubscribe().catch(() => false);
        setCurrent(null);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      load();
    }
  };

  const sendTest = async () => {
    setError(null);
    setMessage(null);
    try {
      const r = await api.post("/api/me/push/test");
      const sent = (r && r.sent) || 0;
      setMessage(`Sent to ${sent} device${sent === 1 ? "" : "s"}.`);
    } catch (e) {
      setError(e.message);
    }
  };

  const hint = support === "unsupported"
    ? null
    : support === "blocked"
      ? "You have blocked notifications for the portal in this browser. Allow them in the "
        + "browser's site settings, then come back here."
      : `A ${word()} to sign, results ready, and a policy waiting for you — beside the e-mail, `
        + "never instead of it. A policy's notification goes when its e-mail is queued, so it "
        + "can arrive before the e-mail does.";

  return (
    <Card style={{ marginBottom: 20 }}>
      <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>
        Notifications on this device
      </h3>
      {support === "unsupported" ? (
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>
          This browser cannot receive notifications. On an iPhone or iPad, add the portal to the
          Home Screen and open it from there.
        </p>
      ) : (
        <div style={{ margin: "8px 0 12px" }}>
          <SwitchRow label="Notifications on this device" checked={on} onChange={toggle}
                     hint={hint} disabled={busy || !keyRead || !subRead || support === "blocked"} />
        </div>
      )}
      {error && (
        <div role="alert" style={{ margin: "0 0 12px", padding: "10px 12px", borderRadius: 8,
                                   background: "rgba(220,38,38,0.06)",
                                   border: "1px solid rgba(220,38,38,0.25)",
                                   color: "#b91c1c", fontSize: 13 }}>{error}</div>
      )}
      {message && (
        <p role="status" style={{ margin: "0 0 12px", fontSize: 13, color: "#374151" }}>{message}</p>
      )}
      {rows.length > 0 && (
        <>
          <ul style={{ listStyle: "none", margin: "0 0 12px", padding: 0,
                       display: "flex", flexDirection: "column", gap: 8 }}>
            {rows.map(r => (
              <li key={r.id}
                  style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px",
                           display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 14 }}>{deviceName(r.user_agent)}</span>
                {current && r.endpoint === current.endpoint && <Badge color="green">This device</Badge>}
                <span style={{ color: "#6b7280", fontSize: 12 }}>
                  added {formatWhen(r.created_at, zone)}
                </span>
                {r.last_success_at && (
                  <span style={{ color: "#6b7280", fontSize: 12 }}>
                    last reached {formatWhen(r.last_success_at, zone)}
                  </span>
                )}
                <span style={{ flex: 1 }} />
                <Btn onClick={() => remove(r)} style={{ padding: "4px 12px", fontSize: 13 }}>
                  Remove
                </Btn>
              </li>
            ))}
          </ul>
          <Btn onClick={sendTest}>Send a test</Btn>
        </>
      )}
    </Card>
  );
}


export default function ProfilePage() {
  const { user } = useAuth();
  // Until the facility confirms a self-registered account, the server answers
  // it nothing but its own profile (2026-09-28): the documents, reopen requests
  // and newsletter cards would each be a request refused 403.
  const confirmed = profileConfirmed(user);
  return (
    <PortalLayout>
      <h1 style={{ margin: "0 0 4px", fontSize: 26, fontWeight: 700 }}>
        Personal Administration
      </h1>
      <p style={{ margin: "0 0 24px", color: "#6b7280", fontSize: 14 }}>
        {user?.full_name || user?.email}
        {" · "}
        {user?.email || "no e-mail address on file"}
      </p>
      <AccountCard />
      {confirmed && (
        <>
          <div style={{ height: 20 }} />
          <OutstandingDocuments />
          <ReopenRequests />
          <DocumentChanges />
          <div style={{ height: 20 }} />
          <NewsletterSubscriptions />
          <div style={{ height: 20 }} />
          <PushOnThisDevice />
        </>
      )}
    </PortalLayout>
  );
}
